import { Link } from "wouter";

export interface NetworkRouterCard {
  /** Primary label. When `description` is absent, this is the clickable text. */
  label: string;
  /** Optional supporting line shown under the label. */
  description?: string;
  /** Destination. Internal paths (starting with "/") use wouter; others render as anchors. */
  href: string;
  /** Optional call-to-action text for the link when a description is present. Defaults to "Visit". */
  cta?: string;
}

interface NetworkRouterProps {
  eyebrow: string;
  heading?: string;
  intro?: string;
  cards: NetworkRouterCard[];
  /** Grid columns at the medium breakpoint. */
  columns?: 2 | 3;
  /** Visual treatment. "dark" inverts colors for dark section backgrounds. */
  variant?: "light" | "dark";
  className?: string;
}

function CardLink({
  href,
  children,
  className,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
}) {
  const isInternal = href.startsWith("/");
  if (isInternal) {
    return (
      <Link href={href} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
}

export function NetworkRouter({
  eyebrow,
  heading,
  intro,
  cards,
  columns = 2,
  variant = "light",
  className,
}: NetworkRouterProps) {
  const isDark = variant === "dark";
  const gridCols = columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2";

  return (
    <section
      aria-label={heading ?? eyebrow}
      className={`network-router ${className ?? ""}`}
    >
      <span
        className={`text-xs font-bold tracking-[0.25em] uppercase ${
          isDark ? "text-[#58B8B1]" : "text-accent"
        }`}
      >
        {eyebrow}
      </span>
      {heading ? (
        <h2
          className={`mt-4 text-2xl md:text-3xl font-serif leading-tight ${
            isDark ? "text-white" : "text-primary"
          }`}
        >
          {heading}
        </h2>
      ) : null}
      {intro ? (
        <p
          className={`mt-4 max-w-2xl font-light leading-relaxed ${
            isDark ? "text-gray-300" : "text-secondary"
          }`}
        >
          {intro}
        </p>
      ) : null}

      <div className={`mt-8 grid grid-cols-1 ${gridCols} gap-4`}>
        {cards.map((card, i) => {
          const hasDescription = Boolean(card.description);
          return (
            <div
              key={`${card.href}-${i}`}
              className={`group flex flex-col gap-2 border p-5 transition-colors ${
                isDark
                  ? "border-white/15 bg-white/5 hover:border-[#58B8B1]"
                  : "border-border bg-background hover:border-accent"
              }`}
            >
              {hasDescription ? (
                <>
                  <span
                    className={`font-serif text-lg ${
                      isDark ? "text-white" : "text-primary"
                    }`}
                  >
                    {card.label}
                  </span>
                  <p
                    className={`text-sm font-light leading-relaxed ${
                      isDark ? "text-gray-300" : "text-secondary"
                    }`}
                  >
                    {card.description}
                  </p>
                  <CardLink
                    href={card.href}
                    className={`mt-1 inline-flex items-center gap-1 text-sm font-medium transition-colors ${
                      isDark
                        ? "text-[#58B8B1] hover:text-[#7fd0c9]"
                        : "text-accent hover:text-primary"
                    }`}
                  >
                    {card.cta ?? "Visit"} <span aria-hidden="true">&rarr;</span>
                  </CardLink>
                </>
              ) : (
                <CardLink
                  href={card.href}
                  className={`inline-flex items-center gap-1 font-medium transition-colors ${
                    isDark
                      ? "text-[#58B8B1] hover:text-[#7fd0c9]"
                      : "text-accent hover:text-primary"
                  }`}
                >
                  {card.label} <span aria-hidden="true">&rarr;</span>
                </CardLink>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
